import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/', label: '首页', icon: 'ri-home-5-line' },
  { to: '/category', label: '漫画分类', icon: 'ri-grid-fill' },
  { to: '/manga/m1', label: '健身教练', icon: 'ri-fire-line' },
  { to: '/about', label: '关于我们', icon: 'ri-information-line' },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = pathname === '/';
  const transparent = isHome && !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) {
      navigate(`/category?q=${encodeURIComponent(q)}`);
    }
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-transparent text-background-50'
          : 'border-b border-background-200 bg-background-50/95 text-foreground-950 backdrop-blur-md'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap font-heading text-xl md:text-2xl">
          <span className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg ${transparent ? 'bg-primary-500 text-background-50' : 'bg-primary-500 text-background-50'}`}>
            <i className="ri-book-2-fill" aria-hidden="true" />
          </span>
          <span className="font-heading">
            073
            <span className="text-primary-500">漫画网</span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => {
            const active = pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors ${
                  active
                    ? transparent
                      ? 'bg-background-50/20 font-medium text-background-50'
                      : 'bg-primary-100 font-medium text-primary-700'
                    : transparent
                      ? 'text-background-50/90 hover:bg-background-50/15 hover:text-background-50'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-primary-700'
                }`}
              >
                <i className={link.icon} aria-hidden="true" />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <form onSubmit={submitSearch} role="search" className="relative">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画…"
              aria-label="搜索漫画"
              className={`h-9 w-44 rounded-full border py-1.5 pl-9 pr-4 text-sm outline-none transition-all focus:w-56 lg:w-52 ${
                transparent
                  ? 'border-background-50/40 bg-background-50/15 text-background-50 placeholder:text-background-50/70 focus:border-background-50/70'
                  : 'border-background-200 bg-background-100 text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400'
              }`}
            />
          </form>
          <Link
            to="/manga/m1"
            className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            立即阅读
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          aria-expanded={menuOpen}
          className={`flex h-10 w-10 items-center justify-center rounded-md text-xl md:hidden ${
            transparent ? 'text-background-50' : 'text-foreground-950'
          }`}
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} aria-hidden="true" />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50/98 px-4 pb-4 pt-2 md:hidden">
          <nav aria-label="移动端导航" className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                <i className={link.icon} aria-hidden="true" />
                {link.label}
              </Link>
            ))}
          </nav>
          <form onSubmit={submitSearch} role="search" className="relative mt-3">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画…"
              aria-label="搜索漫画"
              className="h-10 w-full rounded-full border border-background-200 bg-background-100 py-2 pl-9 pr-4 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
          </form>
        </div>
      )}
    </header>
  );
}